import { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectModal } from './ProjectModal';
import { addProject } from './ProjectHelpers';
import { v4 as uuidv4 } from 'uuid';
import ProjectsAPI from '../../../services/api/admin/projects-api';
import TradesAPI from '../../../services/api/admin/trades-api';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { AuthContext } from '../../context/auth-context';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import ACCAPI from '../../../services/api/integrations/acc-api';
import ShopSyncAPI from '../../../services/api/integrations/shop-sync-api';
import { ShopExceptionsDialog } from './ShopExceptionsDialog';
import ProductionMetricsAPI from '../../../services/api/analytics/production-metrics-api';
import Logger from '../../../utils/logger';
import { projectActions, writeStoredProject } from '../../../store/project';
import { getProjectDateRangeErrorMessage } from '../../../utils/projectDateRangeError';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [trades, setTrades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editProject, setEditProject] = useState(false);
  const [currentProject, setCurrentProject] = useState({
    id: '',
    name: '',
    number: '',
    default_trade_id: '',
    active: true,
  });
  const [syncSubmitted, setSyncSubmitted] = useState({});
  const [recalcPlannedLoading, setRecalcPlannedLoading] = useState({});
  const [shopExceptionCounts, setShopExceptionCounts] = useState({});
  const [shopExceptionsDialog, setShopExceptionsDialog] = useState({
    open: false,
    projectId: null,
    projectName: '',
  });

  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const showRecalculatePlannedDatesButton = !!auth.isSuperAdmin;

  useEffect(() => {
    const getProjects = async () => {
      if (auth.token) {
        try {
          Logger.debug('Fetching projects', { hasToken: !!auth.token });
          const projectsData = await ProjectsAPI.getAll(auth.token);
          Logger.info('Projects retrieved successfully', {
            count: projectsData?.length,
            isArray: Array.isArray(projectsData),
          });
          setProjects(projectsData);
        } catch (err) {
          Logger.error('Error getting projects', err, {
            response: err.response?.data,
          });
        }
      }
    };
    getProjects();
  }, [auth.token]);

  // Per-project unresolved shop-sync exception counts (for the grid badge).
  const loadShopExceptionCounts = async () => {
    if (!auth.token) return;
    try {
      const counts = await ShopSyncAPI.getExceptionCounts(auth.token);
      const map = {};
      counts.forEach((c) => {
        map[c.fieldtime_project_id] = c.count;
      });
      setShopExceptionCounts(map);
    } catch (err) {
      Logger.error('Error loading shop exception counts', err);
    }
  };

  useEffect(() => {
    loadShopExceptionCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token]);

  // Fetch trades on mount
  useEffect(() => {
    const fetchTrades = async () => {
      if (auth.token) {
        try {
          const tradesData = await TradesAPI.getAll(auth.token);
          setTrades(tradesData);
        } catch (err) {
          Logger.error('Error fetching trades', err);
          setTrades([]);
        }
      }
    };
    fetchTrades();
  }, [auth.token]);

  const handleOpen = () => {
    setEditProject(false);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentProject({
      id: '',
      name: '',
      number: '',
      default_trade_id: '',
      active: true,
    });
  };

  const handleAddProject = async (project) => {
    const id = uuidv4();
    const newProject = { ...project, id: id };
    Logger.userAction('Adding new project', { project: newProject });

    const newProjectList = [...projects, newProject];
    const sortedProjectList = newProjectList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
          ? -1
          : 0
    );
    setProjects(sortedProjectList);

    setCurrentProject({
      id: '',
      name: '',
      number: '',
      default_trade_id: '',
      active: true,
    });

    setOpen(false);

    try {
      const addedProject = await addProject(newProject, auth.token);
      Logger.info('Project added successfully', { project: addedProject });
    } catch (error) {
      Logger.error('Error adding project', error);
      setProjects(projects.filter((project) => project.id !== newProject.id));
    }
  };

  const handleEditProject = (rowSelected) => {
    const project = rowSelected.row;
    setCurrentProject({
      id: project.id,
      name: project.name,
      number: project.number ? project.number : '',
      active: project.active,
      default_trade_id: project.default_trade_id || '',
      acc_schedule_enabled: project.acc_schedule_enabled || false,
      shop_sync_enabled: project.shop_sync_enabled || false,
      acc_asset_sync_enabled: project.acc_asset_sync_enabled || false,
      kpi_earned_over_actual: project.kpi_earned_over_actual || false,
      autodesk_project_id: project.autodesk_project_id || null,
      acc_last_sync_at: project.acc_last_sync_at || null,
      acc_last_sync_status: project.acc_last_sync_status || null,
      acc_last_sync_error: project.acc_last_sync_error || null,
    });
    Logger.userAction('Editing project', { project });
    setEditProject(true);
    setOpen(true);
  };

  // Patch a project in the grid list in place, without closing the modal.
  // Used when a field (e.g. shop_sync_enabled) is persisted from inside the
  // modal immediately on change rather than via the "Update Project" submit.
  const handleProjectFieldPersisted = (updatedProject) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === updatedProject.id ? { ...p, ...updatedProject } : p
      )
    );
    setCurrentProject((prev) =>
      prev && prev.id === updatedProject.id
        ? { ...prev, ...updatedProject }
        : prev
    );
  };

  const handleUpdateProject = async (updatedProject) => {
    Logger.userAction('Updating project', { project: updatedProject });

    setOpen(false);

    const previousProjects = projects;

    // Optimistically update the UI
    const updatedProjects = projects.map((project) => {
      if (project.id === updatedProject.id) {
        return {
          ...project,
          ...updatedProject,
        };
      } else {
        return project;
      }
    });

    setProjects(updatedProjects);

    setCurrentProject({
      id: '',
      name: '',
      number: '',
      default_trade_id: '',
      active: true,
    });

    try {
      const modifiedProject = await ProjectsAPI.update(
        updatedProject,
        auth.token
      );
      Logger.info('Project updated successfully', { project: modifiedProject });

      // The selected project is a snapshot. Refresh it now so the sidebar and
      // New Package pick up this edit without a reselect or page reload.
      if (selectedProject?.id === updatedProject.id) {
        const saved = { ...updatedProject, ...modifiedProject };
        const payload = {
          id: saved.id,
          number: saved.number,
          name: saved.name,
          default_trade_id: saved.default_trade_id || null,
        };
        dispatch(projectActions.selectProject(payload));
        writeStoredProject(auth.userId, payload);
      }
    } catch (err) {
      Logger.error('Error updating project', err);
      alert(err.response?.data?.message || err.message);
      setProjects(previousProjects);
    }
  };

  const handleScheduleSync = async (projectId) => {
    Logger.userAction('Triggering schedule sync', { projectId });
    setSyncSubmitted((prev) => ({ ...prev, [projectId]: true }));

    try {
      await ACCAPI.triggerScheduleSync(projectId, auth.token);
      Logger.info('Schedule sync submitted successfully', { projectId });
    } catch (err) {
      Logger.error('Error triggering schedule sync', err);
      alert(
        err.response?.data?.message ||
          err.message ||
          'Failed to submit schedule sync'
      );
      setSyncSubmitted((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const handleRecalculatePlannedDatesFromSchedule = async (projectId) => {
    Logger.userAction('Recalculating planned dates from schedule (DB only)', {
      projectId,
    });
    setRecalcPlannedLoading((prev) => ({ ...prev, [projectId]: true }));
    try {
      const result =
        await ProductionMetricsAPI.recalculatePlannedDatesFromSchedule(
          projectId,
          auth.token
        );
      Logger.info('Recalculate planned dates from schedule complete', {
        projectId,
        result,
      });
      alert(
        `Planned dates from schedule:\n` +
          `Processed: ${result.packagesProcessed}\n` +
          `Updated: ${result.packagesUpdated}\n` +
          `Skipped (unchanged): ${result.packagesSkipped}\n` +
          `Missing link: ${result.packagesMissingLink}`
      );
    } catch (err) {
      Logger.error('Error recalculating planned dates from schedule', err);
      alert(
        getProjectDateRangeErrorMessage(
          err,
          'Failed to recalculate planned dates from schedule'
        )
      );
    } finally {
      setRecalcPlannedLoading((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  // Format sync status for display
  const formatSyncStatus = (params) => {
    const status = params.row.acc_last_sync_status;
    const lastSyncAt = params.row.acc_last_sync_at;
    const error = params.row.acc_last_sync_error;

    if (!status && !lastSyncAt) {
      return 'Never synced';
    }

    if (status === 'in_progress') {
      return 'Syncing...';
    }

    if (status === 'success') {
      const date = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : '';
      return `Success - ${date}`;
    }

    if (status === 'failed') {
      const date = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : '';
      const errorMsg = error
        ? error.length > 50
          ? error.substring(0, 50) + '...'
          : error
        : '';
      return `Failed - ${date}${errorMsg ? ` (${errorMsg})` : ''}`;
    }

    return status || 'Unknown';
  };

  const formatScheduleSyncStatus = (row) => {
    if (!row.acc_schedule_enabled) return null;

    const status = row.acc_schedule_last_sync_status;
    const lastSynced = row.acc_schedule_last_synced;

    if (!status && !lastSynced) return 'Never synced';
    if (status === 'pending') return 'Sync pending';
    if (status === 'processing') return 'Processing...';
    if (status === 'completed') {
      return lastSynced
        ? `Synced: ${new Date(lastSynced).toLocaleString()}`
        : 'Completed';
    }
    if (status === 'no_changes') {
      return lastSynced
        ? `No changes: ${new Date(lastSynced).toLocaleString()}`
        : 'No changes detected';
    }
    if (status === 'failed') return 'Failed';
    return status || 'Unknown';
  };

  const columns = [
    {
      field: 'number',
      headerName: 'Project Number',
      width: 200,
      editable: false,
    },
    {
      field: 'name',
      headerName: 'Project Name',
      flex: 1,
      minWidth: 200,
      editable: false,
    },
    {
      field: 'default_trade_id',
      headerName: 'Trade',
      width: 200,
      editable: false,
      valueGetter: (params) => {
        const trade = trades.find((t) => t.id === params.row.default_trade_id);
        return trade ? trade.name : '';
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 130,
      type: 'boolean',
    },
    {
      field: 'acc_last_sync_status',
      headerName: 'Last ACC Sync',
      width: 300,
      editable: false,
      valueGetter: (params) => formatSyncStatus(params),
      renderCell: (params) => {
        const status = params.row.acc_last_sync_status;
        let color = '#666';
        if (status === 'success') color = '#4caf50';
        if (status === 'failed') color = '#d32f2f';
        if (status === 'in_progress') color = '#ff9800';

        return <span style={{ color }}>{formatSyncStatus(params)}</span>;
      },
    },
    {
      field: 'acc_schedule_last_sync_status',
      headerName: 'Schedule Sync',
      width: 300,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row.acc_schedule_enabled) return null;

        const submitted = syncSubmitted[row.id];
        const status = row.acc_schedule_last_sync_status;

        const recalcLoading = recalcPlannedLoading[row.id];

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              py: 0.5,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<SyncIcon />}
                onClick={() => handleScheduleSync(row.id)}
                disabled={
                  submitted || status === 'pending' || status === 'processing'
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                {submitted ? 'Sync Submitted' : 'Sync Schedule'}
              </Button>

              {showRecalculatePlannedDatesButton && (
                <Tooltip
                  arrow
                  describeChild
                  title={
                    <>
                      Recalculates package duration and planned field finish
                      dates from schedule activities already stored in the
                      database (no new ACC sync).
                      <br />
                      <br />
                      Use when schedule data is in the DB but has not been
                      synced recently—for example after manual field start
                      changes, or when sync reported no schedule changes.
                      <br />
                      <br />
                      If you just ran Sync Schedule with a new schedule version,
                      this is redundant; sync already updates these values.
                    </>
                  }
                  componentsProps={{
                    tooltip: {
                      sx: { maxWidth: 360, fontSize: '0.8125rem' },
                    },
                  }}
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<CalculateOutlinedIcon />}
                      onClick={() =>
                        handleRecalculatePlannedDatesFromSchedule(row.id)
                      }
                      disabled={
                        recalcLoading ||
                        status === 'pending' ||
                        status === 'processing'
                      }
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {recalcLoading ? 'Recalculating…' : 'Recalc planned (DB)'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                minWidth: 0,
                justifyContent: 'center',
                alignSelf: 'stretch',
              }}
            >
              {submitted && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2 }}
                >
                  Refresh page to check status
                </Typography>
              )}

              {!submitted && status === 'pending' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2 }}
                >
                  Pending — refresh page
                </Typography>
              )}

              {!submitted && status === 'processing' && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ lineHeight: 1.2 }}
                >
                  Processing...
                </Typography>
              )}

              {!submitted &&
                (status === 'completed' || status === 'no_changes') && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {formatScheduleSyncStatus(row)}
                  </Typography>
                )}

              {!submitted && status === 'no_changes' && (
                <Chip
                  label="No changes"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}

              {!submitted && status === 'failed' && (
                <Chip
                  label="Failed"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'shop_sync',
      headerName: 'Shop Time Sync',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row.shop_sync_enabled) return null;
        const count = shopExceptionCounts[row.id] || 0;
        if (count === 0) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Tooltip title="Unresolved exceptions — click to view">
              <Chip
                size="small"
                color="warning"
                label={`${count} exception${count === 1 ? '' : 's'}`}
                onClick={() =>
                  setShopExceptionsDialog({
                    open: true,
                    projectId: row.id,
                    projectName: row.name,
                  })
                }
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: '',
      cellClassName: 'actions',
      getActions: (id) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          name="edit"
          label="Edit"
          onClick={() => {
            handleEditProject(id);
          }}
        />,
      ],
    },
  ];

  return (
    <div>
      <Box
        sx={{
          width: '75%',
          padding: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="contained" onClick={() => handleOpen()}>
          Add Project
        </Button>
      </Box>

      <Box
        sx={{
          width: '96%',
          height: '80vh',
          // height: 771 9/5/24 this was set at a fixed height and then I increased the nav bar
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <ProjectModal
          project={currentProject}
          editProject={editProject}
          open={open}
          handleCancel={handleCancel}
          handleAddProject={handleAddProject}
          handleUpdateProject={handleUpdateProject}
          handleProjectFieldPersisted={handleProjectFieldPersisted}
          shopExceptionCount={
            currentProject?.id ? shopExceptionCounts[currentProject.id] || 0 : 0
          }
          onViewExceptions={() =>
            setShopExceptionsDialog({
              open: true,
              projectId: currentProject.id,
              projectName: currentProject.name,
            })
          }
          refreshExceptionCounts={loadShopExceptionCounts}
        />
        <ShopExceptionsDialog
          open={shopExceptionsDialog.open}
          projectId={shopExceptionsDialog.projectId}
          projectName={shopExceptionsDialog.projectName}
          token={auth.token}
          onClose={() =>
            setShopExceptionsDialog({
              open: false,
              projectId: null,
              projectName: '',
            })
          }
          onResolved={loadShopExceptionCounts}
        />
        <DataGrid
          getRowId={(row) => row.id}
          columns={columns}
          rows={projects}
          components={{ Toolbar: GridToolbar }}
          autoPageSize
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              py: 0.5,
            },
          }}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
            },
          }}
          disableDensitySelector={true}
          disableColumnSelector={true}
          initialState={{
            filter: {
              filterModel: {
                items: [
                  {
                    columnField: 'active',
                    operatorValue: 'is',
                    value: 'true',
                  },
                ],
              },
            },
          }}
        />
      </Box>
    </div>
  );
};
